import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { useComponentConfigClient } from './componentConfig.client';
import AddComponentModal from './AddComponentModal';
import { DeleteButton } from '../components/DeleteButton';
import PageLayout from '../components/PageLayout';
import { formatDate } from '../date.format';
import { Cpu, Search, ArrowUpDown } from 'lucide-react';
const TYPE_LABELS = {
    audio: 'Audio',
    file: 'File',
    camera: 'Camera',
    chatbot: 'Chatbot',
};
const TYPE_COLORS = {
    audio: 'bg-purple-100 text-purple-700',
    file: 'bg-blue-100 text-blue-700',
    camera: 'bg-green-100 text-green-700',
    chatbot: 'bg-orange-100 text-orange-700',
};
const ComponentConfigsPage = () => {
    const client = useComponentConfigClient();
    const navigate = useNavigate();
    const [components, setComponents] = useState([]);
    const [error, setError] = useState();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const filteredComponents = useMemo(() => {
        return components
            .filter(c => {
            const q = searchQuery.toLowerCase();
            return c.componentId.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
        })
            .sort((a, b) => {
            switch (sortBy) {
                case 'newest': return b.createdAt - a.createdAt;
                case 'oldest': return a.createdAt - b.createdAt;
                case 'az': return a.name.localeCompare(b.name);
                case 'za': return b.name.localeCompare(a.name);
                default: return 0;
            }
        });
    }, [components, searchQuery, sortBy]);
    useEffect(() => {
        client.listComponents()
            .then(setComponents)
            .catch(() => setError('Failed to load components. Please try again.'));
    }, []);
    const handleCreate = async (data) => {
        await client.createComponent(data);
        const updated = await client.listComponents();
        setComponents(updated);
        navigate(`/components/${data.componentId}`);
    };
    const handleDelete = async (componentId) => {
        try {
            await client.deleteComponent(componentId);
            setComponents(prev => prev.filter(c => c.componentId !== componentId));
        }
        catch {
            setError(`Failed to delete component ${componentId}.`);
        }
    };
    return (React.createElement(PageLayout, { title: "AI Components", subtitle: "Manage AI-powered components. Attach a componentId to enable monitoring and fine-tuning.", icon: React.createElement(Cpu, { className: "w-7 h-7 text-gray-700" }), error: error, headerActions: React.createElement(Button, { primary: true, onClick: () => setModalOpen(true) },
            React.createElement(Icon, { name: "plus" }),
            " New Component"), filterSection: React.createElement("div", { className: "flex flex-col md:flex-row items-start md:items-center justify-between gap-4" },
            React.createElement("div", { className: "relative w-full md:w-96" },
                React.createElement("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" },
                    React.createElement(Search, { className: "h-4 w-4 text-gray-400" })),
                React.createElement("input", { type: "text", placeholder: "Search components...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none hover:border-gray-300 transition-colors" })),
            React.createElement("div", { className: "relative" },
                React.createElement("div", { className: "absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none" },
                    React.createElement(ArrowUpDown, { className: "h-3.5 w-3.5 text-gray-500" })),
                React.createElement("select", { value: sortBy, onChange: (e) => setSortBy(e.target.value), className: "pl-8 pr-8 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none appearance-none cursor-pointer hover:border-gray-300 transition-colors" },
                    React.createElement("option", { value: "newest" }, "Newest First"),
                    React.createElement("option", { value: "oldest" }, "Oldest First"),
                    React.createElement("option", { value: "az" }, "Name A-Z"),
                    React.createElement("option", { value: "za" }, "Name Z-A")))) },
        React.createElement("div", { className: "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" },
            React.createElement("div", { className: "grid grid-cols-12 gap-4 px-6 py-3 bg-gray-700 text-white text-base font-medium" },
                React.createElement("div", { className: "col-span-3" }, "ID"),
                React.createElement("div", { className: "col-span-3" }, "NAME"),
                React.createElement("div", { className: "col-span-2" }, "TYPE"),
                React.createElement("div", { className: "col-span-3" }, "CREATED AT"),
                React.createElement("div", { className: "col-span-1 text-right" }, "ACTIONS")),
            React.createElement("div", { className: "divide-y divide-gray-200" },
                filteredComponents.length === 0 && (React.createElement("div", { className: "px-6 py-12 text-center text-gray-500" }, searchQuery ? (React.createElement(React.Fragment, null,
                    React.createElement(Icon, { name: "search", size: "big", className: "mb-3 opacity-50" }),
                    React.createElement("p", { className: "text-lg font-medium" }, "No components found"),
                    React.createElement("p", { className: "text-sm" },
                        "No components match \"",
                        searchQuery,
                        "\""))) : (React.createElement(React.Fragment, null,
                    React.createElement(Cpu, { className: "w-10 h-10 mx-auto mb-3 opacity-30" }),
                    React.createElement("p", { className: "text-lg font-medium" }, "No components yet"),
                    React.createElement("p", { className: "text-sm" }, "Create your first AI component to get started."))))),
                filteredComponents.map((c) => (React.createElement("div", { key: c.componentId, className: "grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors group" },
                    React.createElement("div", { className: "col-span-3" },
                        React.createElement(Link, { to: `/components/${c.componentId}`, className: "!text-orange-600 font-medium !text-base hover:underline" }, c.componentId)),
                    React.createElement("div", { className: "col-span-3" },
                        React.createElement("span", { className: "text-gray-900 text-base" }, c.name)),
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement("span", { className: `inline-block px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[c.componentType]}` }, TYPE_LABELS[c.componentType])),
                    React.createElement("div", { className: "col-span-3" },
                        React.createElement("span", { className: "text-gray-600 text-base" }, formatDate(c.createdAt))),
                    React.createElement("div", { className: "col-span-1 flex items-center justify-end space-x-2" },
                        React.createElement("button", { className: "p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors", title: "Manage", onClick: () => navigate(`/components/${c.componentId}`) },
                            React.createElement(Icon, { name: "edit" })),
                        React.createElement(DeleteButton, { iconOnly: true, onConfirm: () => handleDelete(c.componentId), title: "Delete Component", message: `Are you sure you want to delete "${c.name}"? This will stop all interaction logging.` }))))))),
        React.createElement(AddComponentModal, { open: modalOpen, onClose: () => setModalOpen(false), onSave: handleCreate })));
};
export default ComponentConfigsPage;
